import { IconCrosshair, IconTrash } from "@tabler/icons-react";
import axios from "axios";
import { useState, useRef } from "react";
import toast from "react-hot-toast";
import { NotificationsProvider, showNotification } from '@mantine/notifications';
import { useForm } from "@mantine/form";
import { createEmotionCache, Container, Paper, ColorSchemeProvider, MantineProvider, Button, TextInput, Center, CheckIcon, Text, Textarea, Select, Checkbox, Group } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import useEmblaCarousel from 'embla-carousel-react';
import { Route, BrowserRouter, Routes, useNavigate } from "react-router-dom";
import AppLayout  from "./components/AppLayout";
import Map from "./components/Map";
import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";
import content from './ambassadorForm.json';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-markercluster/styles'
import L from "leaflet";

const googleMarkerIcon = new L.Icon({
    iconUrl:
      "https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png",
    iconSize: [27, 40],
    iconAnchor: [13, 40],
    popupAnchor: [0, -40],
  });

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          resolve({ latitude, longitude });
        },
        (error) => {
          reject(error);
        }
      );
    } else {
      reject(new Error('Geolocation is not supported by this browser.'));
    }
  });
}


function App() {
  const [colorScheme, setColorScheme] = useLocalStorage({
    key: "_oo_theme",
    defaultValue: "light",
    getInitialValueInEffect: true
  });

  const toggleColorScheme = (value) => {
    setColorScheme(value || (colorScheme === "light" ? "dark" : "light"));
  };

  const myCache = createEmotionCache({ key: "ooemc"});

  const HomePage = () => {
    const [loading, setLoading] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const scroll = (direction) => {
      if (emblaApi) {
        if (direction === 'prev') {
          emblaApi.scrollPrev();
        } else {
          emblaApi.scrollNext();
        }
      }
    };

    const form = useForm({
      initialValues: {
        name: "",
        category: "",
        trainingType: "",
        story: "",
        link: "",
        email: "",
        latitude: "",
        longitude: "",
        termsAccepted: false,
      },
      validate: {
        name: (value) => value.length > 0 ? null : "Name is required",
        category: (value) => value && value.length > 0 ? null : "Please select a participant category",
        trainingType: (value) => value && value.length > 0 ? null : "Please select a training program",
        story: (value) => {
          if (value.length === 0) return "Implementation story is required";
          if (value.length > 300) return "Story must not exceed 300 characters";
          return null;
        },
        longitude: (value) => value.length > 0 ? null : "Invalid longitude value",
        latitude: (value) => value.length > 0 ? null : "Invalid latitude",
        termsAccepted: (value) => value ? null : "You must accept the terms and conditions",
      }
    });

    const getCoords = () => {
      if (!navigator.geolocation) {
        showNotification({
          title: "Not Supported",
          message: "Geolocation is not supported by your browser.",
          color: "red",
        });
        return;
      }

      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "denied") {
          showNotification({
            title: "Location Access Blocked",
            message: "Please enable location access in your browser or device settings. You can also pin your location manually on the map.",
            color: "red",
          });
          return;
        }

        getCurrentPosition()
          .then((position) => {
            form.setFieldValue("latitude", position?.latitude.toString());
            form.setFieldValue("longitude", position.longitude.toString());
            showNotification({
              title: "Success",
              message: "Location captured successfully",
              color: "green",
            });
          })
          .catch((error) => {
            console.error("Error getting position:", error);
            showNotification({
              title: "Location Error",
              message: "Unable to retrieve your location. Please pin it manually on the map.",
              color: "red",
            });
          });
      });
    };

    const handlePhotoSelect = (e) => {
      const files = Array.from(e.target.files);
      
      if (photos.length + files.length > 3) {
        showNotification({
          title: "Too Many Photos",
          message: "Maximum 3 photos allowed",
          color: "red",
        });
        return;
      }

      const newPhotos = files.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));

      setPhotos([...photos, ...newPhotos]);
    };

    const removePhoto = (index) => {
      const newPhotos = photos.filter((_, i) => i !== index);
      setPhotos(newPhotos);
    };

    const onSubmit = async (obj) => {
      if (photos.length === 0) {
        showNotification({
          title: "No Photos",
          message: "Please upload at least one photo",
          color: "red",
        });
        return;
      }

      if (photos.length > 3) {
        showNotification({
          title: "Too Many Photos",
          message: "Maximum 3 photos allowed",
          color: "red",
        });
        return;
      }

      setLoading(true);
      try {
        // Step 1: Upload all photos to upload_image.php and collect file paths
        const uploadedPhotoPaths = [];
        
        for (let i = 0; i < photos.length; i++) {
          const photoFormData = new FormData();
          photoFormData.append("photo", photos[i].file);

          const uploadResponse = await axios.post(
            "https://mindfulafrican.org/poi_server/upload_image.php",
            photoFormData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (!uploadResponse.data.success) {
            throw new Error(`Failed to upload photo ${i + 1}: ${uploadResponse.data.message}`);
          }

          uploadedPhotoPaths.push(uploadResponse.data.filePath);
          console.log(`Photo ${i + 1} uploaded: ${uploadResponse.data.filePath}`);
        }

        // Step 2: Submit form data with uploaded photo paths to create_poi.php
        const submitData = {
          name: obj.name,
          category: obj.category,
          trainingType: obj.trainingType,
          story: obj.story,
          link: obj.link || null,
          email: obj.email || null,
          latitude: obj.latitude,
          longitude: obj.longitude,
          photoPaths: uploadedPhotoPaths // Array of uploaded file paths
        };

        const submitResponse = await axios.post(
          "https://mindfulafrican.org/poi_server/create_poi.php",
          submitData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!submitResponse.data.success) {
          throw new Error(submitResponse.data.message);
        }

        setLoading(false);
        toast.success("Data submitted successfully");
        navigate("/thank-you");

      } catch(error) {
        setLoading(false);
        console.error("Error submitting:", error);
        toast.error(error.response?.data?.message || error.message || "An issue occurred while submitting the data");
      }
    };

    const LocationPicker = ({ onLocationSelect }) => {
      useMapEvents({
        dblclick(e) {
          const { lat, lng } = e.latlng;
          form.setFieldValue("latitude", lat.toFixed(6).toString());
          form.setFieldValue("longitude", lng.toFixed(6).toString());
        },
      });
      return null;
    };

    const DraggableMarker = ({ position, onDragEnd }) => {
      const markerRef = useRef(null);
      return (
        <Marker
          icon={googleMarkerIcon}
          position={position}
          draggable={true}
          ref={markerRef}
          eventHandlers={{
            dragend() {
              const marker = markerRef.current;
              if (marker) {
                const { lat, lng } = marker.getLatLng();
                onDragEnd({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
              }
            },
          }}
        />
      );
    };

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    });

    const handleMapLocationUpdate = (lat, lng) => {
      form.setFieldValue("latitude", lat.toString());
      form.setFieldValue("longitude", lng.toString());
    };

    return (
      <Container size={600} py="xl">
        <Paper p="md">
          <form onSubmit={form.onSubmit((values) => { onSubmit(values) })}>
            {/* Organization/School/Individual Name */}
            <TextInput 
              color="#1a235e" 
              size={"md"} 
              mb="md"
              withAsterisk
              {...form.getInputProps("name")} 
              placeholder={content.placeholders.name}
              label={content.fields.name}
              required 
            />

            {/* Participant Category */}
            <Select
              color="#1a235e"
              size={"md"}
              mb="md"
              withAsterisk
              searchable
              clearable
              {...form.getInputProps("category")}
              placeholder="Select a participant category"
              label={content.fields.category}
              data={content.categories.map(cat => ({ value: cat, label: cat }))}
              required
            />

            {/* Training Program */}
            <Select
              color="#1a235e"
              size={"md"}
              mb="md"
              withAsterisk
              searchable
              clearable
              {...form.getInputProps("trainingType")}
              placeholder="Select a training program"
              label={content.fields.trainingType}
              data={content.trainingTypes.map(type => ({ value: type, label: type }))}
              required
            />

            {/* Implementation Story */}
            <div mb="sm">
              <Textarea
                color="#1a235e"
                size={"md"}
                minRows={3}
                mb="md"
                withAsterisk
                {...form.getInputProps("story")}
                placeholder={content.placeholders.story}
                label={content.fields.story}
                required
                maxLength={300}
              />
              <Text size="xs" color="dimmed" align="right">
                {form.values.story.length}/300 {content.labels.characterLimit}
              </Text>
            </div>

            {/* Website or LinkedIn Link */}
            <TextInput 
              color="#1a235e" 
              size={"md"} 
              mb="md"
              {...form.getInputProps("link")} 
              placeholder={content.placeholders.link}
              label={content.fields.link}
            />

            {/* Email Address */}
            <TextInput 
              color="#1a235e" 
              size={"md"} 
              mb="md"
              type="email"
              {...form.getInputProps("email")} 
              placeholder="your.email@example.com"
              label="Email Address"
            />

            {/* Photos Section */}
            <div style={{ marginBottom: "20px" }}>
              <Text weight={500} size="md" mb="md">
                {content.labels.uploadPhotos} <span style={{ color: "red" }}>*</span>
              </Text>
              <Text size="sm" color="dimmed" mb="sm">
                {photos.length}/3 photos uploaded
              </Text>

              {photos.length > 0 && (
                <div className="mb-4 border border-gray-300 rounded-md overflow-hidden" style={{ marginBottom: "15px" }}>
                  <div className="relative" ref={emblaRef}>
                    <div className="flex">
                      {photos.map((photo, index) => (
                        <div
                          key={index}
                          className="flex-shrink-0 w-full flex items-center justify-center bg-gray-100 relative"
                          style={{ minWidth: "100%", height: "50vh" }}
                        >
                          <img
                            src={photo.preview}
                            alt={`${index + 1}`}
                            className="max-h-full max-w-full object-contain"
                          />
                          <Button
                            size="sm"
                            color="red"
                            variant="filled"
                            style={{ position: "absolute", bottom: "10px", right: "10px" }}
                            onClick={() => removePhoto(index)}
                            leftIcon={<IconTrash size={16} />}
                          >
                            Delete
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {photos.length > 1 && (
                    <div className="flex gap-2 p-3 bg-white justify-center">
                      <Button
                        variant="light"
                        color="#1a235e"
                        onClick={() => scroll('prev')}
                        size="xs"
                      >
                        ← Previous
                      </Button>
                      <Button
                        variant="light"
                        color="#1a235e"
                        onClick={() => scroll('next')}
                        size="xs"
                      >
                        Next →
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoSelect}
                style={{ display: "none" }}
              />
              <Button
                variant="outline"
                color="#1a235e"
                onClick={() => fileInputRef.current?.click()}
                disabled={photos.length >= 3}
                fullWidth
              >
                {photos.length >= 3 ? "Maximum photos reached" : "Add Photos"}
              </Button>
            </div>

            {/* Location Section */}
            <Text size={"md"} weight={500} mb="sm">
              {content.map.instruction}
            </Text>
            <Text size={"sm"} color="dimmed" mb="sm">
              {content.map.instruction1}
            </Text>

            <Button 
              color="#1a235e" 
              size={"md"} 
              onClick={() => { getCoords() }} 
              mb="md"
              variant="default"
              leftIcon={<IconCrosshair />}
              fullWidth
            >
              {content.labels.pinLocation}
            </Button>

            {form.values.latitude && form.values.longitude && (
              <Paper p="sm" mb="md" style={{ backgroundColor: "#f0f0f0" }}>
                <Text size="sm">
                  <strong>Latitude:</strong> {form.values.latitude}
                </Text>
                <Text size="sm">
                  <strong>Longitude:</strong> {form.values.longitude}
                </Text>
              </Paper>
            )}

            <Text size={"sm"} color="dimmed" mb="sm">
              {content.map.instruction2}
            </Text>

            <Group mb="md" grow>
              <div className="w-full">
                <MapContainer
                  doubleClickZoom={false}
                  attributionControl={false}
                  center={[15.375592, 36.749879]}
                  zoom={2}
                  style={{ height: "300px", width: "100%", backgroundColor: "white" }}
                >
                  <LocationPicker onLocationSelect={handleMapLocationUpdate} />
                  {form.values.latitude && form.values.longitude && (
                    <DraggableMarker
                      position={[parseFloat(form.values.latitude), parseFloat(form.values.longitude)]}
                      onDragEnd={(coords) => handleMapLocationUpdate(coords.lat, coords.lng)}
                    />
                  )}
                  <TileLayer
                    url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                    subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                    maxZoom={20}
                  />
                </MapContainer>
              </div>
            </Group>

            {/* Terms and Conditions */}
            <Checkbox
              mb="md"
              withAsterisk
              label="I accept the terms and conditions for the map as outlined"
              {...form.getInputProps("termsAccepted", { type: "checkbox" })}
            />

            {/* Submit Button */}
            <Button 
              color="#1a235e" 
              size={"md"} 
              type="submit" 
              loading={loading} 
              mt="lg" 
              fullWidth
              disabled={!form.values.termsAccepted || loading}
            >
              {content.labels.submit}
            </Button>
          </form>
        </Paper>
      </Container>
    );
  };

  const ThankYou = () => {
    const navigate = useNavigate();
    return (
      <Container size={600}>
        <Paper mt={50} p="lg" style={{ textAlign: "center", backgroundColor: "#fafaf8", borderRadius: "12px" }}>
          <Center mb="md">
            <div style={{ 
              backgroundColor: "#d3f9d8", 
              borderRadius: "50%", 
              width: "80px", 
              height: "80px", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center" 
            }}>
              <CheckIcon width={50} height={50} color="#2b8a3e" />
            </div>
          </Center>
          <Text weight={700} size="xl" color="#020101" mb="md">
            Submission Confirmed!
          </Text>
          <Text color="#020101" size="md" mb="lg" style={{ lineHeight: 1.6 }}>
            Thank you for contributing to the Mindful African Initiative map. Your location demonstrates the global reach and impact of SEE Learning and CBCT practices across Africa and beyond.
          </Text>
          <Text size="sm" color="dimmed" mb="xl">
            Your submission has been recorded and will appear on the map after moderation to ensure quality and accuracy.
          </Text>
          <Group mt="xl" mb="md" grow>
            <Button 
              color="blue" 
              size="md"
              onClick={() => navigate("/map")}
            >
              View Map
            </Button>
            <Button 
              color="blue" 
              variant="outline"
              size="md"
              onClick={() => navigate("/new-entry")}
            >
              Add Another
            </Button>
          </Group>
          <Text size="xs" color="dimmed" mt="md">
            Your contribution helps us understand where and how mindful practices are transforming communities.
          </Text>
        </Paper>
      </Container>
    );
  };

  return (
    <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
      <MantineProvider
        theme={{
          colorScheme: colorScheme,
          primaryColor: "indigo",
          primaryShade: 9,
          white: 'white',
          black: 'black',
          cursorType: "pointer",
        }}
        withGlobalStyles
        withNormalizeCSS
        emotionCache={myCache}
      >
        <NotificationsProvider>
          <BrowserRouter basename="/community-map">
            <Routes>
              <Route index element={<Map />} />
              <Route path="/map" element={<Map />} />
              <Route path="/" element={<AppLayout />}>
                <Route path="/new-entry" element={<HomePage />} />
                <Route path="/thank-you" element={<ThankYou />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </NotificationsProvider>
      </MantineProvider>
    </ColorSchemeProvider>
  );
}

export default App;